import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CONTACT_CATEGORY_KEYS, ContactCategory } from 'src/Constants/ContactCategories.constants';

const FAVORITES_PARAM = 'favorites';
const EMERGENCY_PARAM = 'emergency';
const CATEGORIES_PARAM = 'categories';

const VALID_CATEGORY_VALUES = new Set<string>(CONTACT_CATEGORY_KEYS);

interface UseContactFiltersResult {
  favoritesOnly: boolean;
  emergencyOnly: boolean;
  selectedCategories: ContactCategory[];
  setFavoritesOnly: (value: boolean) => void;
  setEmergencyOnly: (value: boolean) => void;
  setSelectedCategories: (next: string[]) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

// Synchronise les filtres du carnet de contacts avec les query params URL
// (?favorites=1&emergency=1&categories=a,b). Les valeurs de catégorie hors enum sont
// ignorées silencieusement pour ne pas crasher sur un deep-link malformé.
const useContactFilters = (): UseContactFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const favoritesOnly = searchParams.get(FAVORITES_PARAM) === '1';
  const emergencyOnly = searchParams.get(EMERGENCY_PARAM) === '1';

  const selectedCategories = useMemo<ContactCategory[]>(() => {
    const raw = searchParams.get(CATEGORIES_PARAM);
    if (!raw) {
      return [];
    }
    return raw
      .split(',')
      .map(value => value.trim())
      .filter((value): value is ContactCategory => VALID_CATEGORY_VALUES.has(value));
  }, [searchParams]);

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const newParams = new URLSearchParams(searchParams);
      mutate(newParams);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setFavoritesOnly = useCallback(
    (value: boolean) => {
      updateParams(params => {
        if (value) {
          params.set(FAVORITES_PARAM, '1');
        } else {
          params.delete(FAVORITES_PARAM);
        }
      });
    },
    [updateParams]
  );

  const setEmergencyOnly = useCallback(
    (value: boolean) => {
      updateParams(params => {
        if (value) {
          params.set(EMERGENCY_PARAM, '1');
        } else {
          params.delete(EMERGENCY_PARAM);
        }
      });
    },
    [updateParams]
  );

  const setSelectedCategories = useCallback(
    (next: string[]) => {
      const cleaned = next.filter(value => VALID_CATEGORY_VALUES.has(value));
      updateParams(params => {
        if (cleaned.length === 0) {
          params.delete(CATEGORIES_PARAM);
        } else {
          params.set(CATEGORIES_PARAM, cleaned.join(','));
        }
      });
    },
    [updateParams]
  );

  const resetFilters = useCallback(() => {
    updateParams(params => {
      params.delete(FAVORITES_PARAM);
      params.delete(EMERGENCY_PARAM);
      params.delete(CATEGORIES_PARAM);
    });
  }, [updateParams]);

  const hasActiveFilters = favoritesOnly || emergencyOnly || selectedCategories.length > 0;

  return {
    favoritesOnly,
    emergencyOnly,
    selectedCategories,
    setFavoritesOnly,
    setEmergencyOnly,
    setSelectedCategories,
    resetFilters,
    hasActiveFilters,
  };
};

export default useContactFilters;
